import React from "react";
import { Printer } from "lucide-react";
import { APPLY_LEAD_DAYS, REMINDER_KINDS, STALE_AFTER_DAYS, URGENT_DAYS } from "@/lib/grantReminders";
import { DIARY_CODES, reminderForDiaryCode } from "@/lib/diaryCodes";

// How the extension and new grant reminders work, for staff. The labels, the timings and the
// diary codes that raise a reminder are read from the files the reminders themselves use
// (src/lib/grantReminders.js, src/lib/diaryCodes.js), so the guide cannot drift from them.

const KIND_STYLES = {
  extension: "bg-orange-100 text-orange-800",
  new_grant: "bg-rose-100 text-rose-800",
  extension_check: "bg-sky-100 text-sky-800",
  follow_up: "bg-violet-100 text-violet-800",
  not_covered: "bg-rose-100 text-rose-800",
  condition: "bg-amber-100 text-amber-900",
};

function Kind({ kind }) {
  return <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${KIND_STYLES[kind]}`}>{REMINDER_KINDS[kind]}</span>;
}

function codesFlagging(flag) {
  return DIARY_CODES.filter((entry) => reminderForDiaryCode(entry) === flag).map((entry) => entry.code);
}

const KIND_MEANINGS = {
  extension: "The grant does not cover the next listing.",
  new_grant: "No grant covers the matter, or the listing is an appeal.",
  extension_check: "The listing may be beyond the grant, or uses the last fee of its kind.",
  not_covered: "The approval letter says the grant will not cover this listing.",
  follow_up: "An extension was requested and no decision is recorded.",
  condition: "A grant condition is still owed, such as proof of means.",
};

function Section({ title, children }) {
  return (
    <section className="break-inside-avoid space-y-1.5">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</h2>
      {children}
    </section>
  );
}

function Row({ when, kind }) {
  return (
    <tr className="border-t border-slate-200 align-top">
      <td className="py-1 pr-3 text-slate-800">{when}</td>
      <td className="py-1 text-right"><Kind kind={kind} /></td>
    </tr>
  );
}

export default function RemindersGuide({ brand = "" }) {
  const firm = brand === "vals" ? "VALS" : brand === "lacw" ? "LACW" : "LACW & VALS";
  const diaryName = brand === "vals" ? "VALS Diary" : brand === "lacw" ? "LACW Aid Status Diary" : "the diaries";
  const checkCodes = codesFlagging("Extension check").join(", ");
  const grantCodes = codesFlagging("New grant").join(", ");
  return (
    <div className="reminders-guide mx-auto max-w-4xl space-y-5 px-4 py-6 text-slate-900 print:max-w-none print:space-y-3 print:p-0">
      <style>{"@media print { .reminders-guide { zoom: 0.74; } }"}</style>
      <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-slate-900 pb-3">
        <div className="max-w-2xl space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-orange-700">{firm} · Staff guide</p>
          <h1 className="text-3xl font-bold print:text-2xl">Grant reminders</h1>
          <p className="text-sm text-slate-600">The portal works out when a matter needs an extension or a new grant applied for, and by when, from what the diary and the Aid Planner already hold. Nothing extra is entered to get a reminder.</p>
        </div>
        <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700 print:hidden">
          <Printer className="h-4 w-4" /> Print
        </button>
      </div>

      <Section title="Where to find them">
        <ul className="grid gap-2 text-sm sm:grid-cols-3 print:grid-cols-3">
          <li className="rounded-md border border-slate-200 px-3 py-2"><strong className="block text-xs">{diaryName}</strong><span className="text-xs text-slate-600">A list at the top, for the week's listings.</span></li>
          <li className="rounded-md border border-slate-200 px-3 py-2"><strong className="block text-xs">Aid Planner dashboard</strong><span className="text-xs text-slate-600">A list at the top, for every matter the firm has.</span></li>
          <li className="rounded-md border border-slate-200 px-3 py-2"><strong className="block text-xs">Each Aid Planner matter</strong><span className="text-xs text-slate-600">The Extension check, for that matter, with the flowchart filled in.</span></li>
        </ul>
      </Section>

      <Section title="What each reminder means">
        <table className="w-full text-left text-xs">
          <tbody>
            {Object.keys(KIND_MEANINGS).map((kind) => (
              <tr key={kind} className="border-t border-slate-200 align-top">
                <td className="w-52 py-1 pr-3"><Kind kind={kind} /></td>
                <td className="py-1 text-slate-700">{KIND_MEANINGS[kind]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <div className="grid gap-x-6 gap-y-4 md:grid-cols-2 print:grid-cols-2">
        <Section title="From the diary">
          <p className="text-xs text-slate-600">Read from each row's funding status and diary code.</p>
          <table className="w-full text-left text-xs">
            <tbody>
              <Row when="Status is Extension required" kind="extension" />
              <Row when="Status is Aid not applied for (and no application on file)" kind="new_grant" />
              <Row when={<>Aid in place, listed as <strong className="font-mono">{checkCodes}</strong></>} kind="extension_check" />
              <Row when={<>Aid in place, listed as <strong className="font-mono">{grantCodes}</strong></>} kind="new_grant" />
            </tbody>
          </table>
        </Section>
        <Section title="From the Aid Planner">
          <p className="text-xs text-slate-600">Read from the uploaded letters, the claims entered and the next listing.</p>
          <table className="w-full text-left text-xs">
            <tbody>
              <Row when="No aid applied for and no grant" kind="new_grant" />
              <Row when="Next listing is beyond the stage the grant covers" kind="extension" />
              <Row when="Every fee of that kind claimed, and another is listed" kind="extension" />
              <Row when="The grant has no fee for the listing" kind="extension" />
              <Row when="Only one fee of that kind is left" kind="extension_check" />
              <Row when="The approval letter excludes the listing" kind="not_covered" />
              <Row when="Extension requested, no decision recorded" kind="follow_up" />
              <Row when="Grant refused" kind="new_grant" />
              <Row when="Proof of means still owed" kind="condition" />
            </tbody>
          </table>
          <p className="text-xs text-slate-500">Finalised or closed matters have no reminders.</p>
        </Section>
      </div>

      <div className="grid gap-x-6 gap-y-4 md:grid-cols-2 print:grid-cols-2">
        <Section title="When to apply">
          <p className="text-xs text-slate-700">Apply at least <strong>{APPLY_LEAD_DAYS} days before the listing</strong>. VLA will not pay for work done before the grant or extension covers it, and a late application can force an adjournment. The list puts the most urgent first:</p>
          <table className="w-full text-left text-xs">
            <tbody>
              <tr className="border-t border-slate-200"><td className="w-24 py-1 font-semibold text-red-800">Overdue</td><td className="py-1 text-slate-700">The listing has passed: a late application.</td></tr>
              <tr className="border-t border-slate-200"><td className="py-1 font-semibold text-red-700">Urgent</td><td className="py-1 text-slate-700">The listing is within {URGENT_DAYS} days.</td></tr>
              <tr className="border-t border-slate-200"><td className="py-1 font-semibold text-amber-800">Due</td><td className="py-1 text-slate-700">The apply-by date has passed; apply now.</td></tr>
              <tr className="border-t border-slate-200"><td className="py-1 font-semibold text-slate-600">Upcoming</td><td className="py-1 text-slate-700">Apply by the date shown.</td></tr>
            </tbody>
          </table>
          <p className="text-xs text-slate-500">A reminder drops off {STALE_AFTER_DAYS} days after its listing. A matter with no listing date shows &ldquo;Listing date unknown — apply now&rdquo;.</p>
        </Section>
        <Section title="What to do with one">
          <ul className="space-y-1.5 text-xs text-slate-700">
            <li><strong>Add to Outlook</strong> saves a calendar entry on the apply-by date (or today, if that has passed), with an alert at 9am.</li>
            <li><strong>Mark as applied</strong> once the application is lodged. In a diary it is kept on that row with the week{brand === "lacw" ? " (for LACW's Aid Status Diary, Complete Law Support marks it on the billing sheet)" : ""}. In the Aid Planner an extension becomes Chase VLA decision; anything else comes back if still a problem at the next listing.</li>
            <li><strong>Open matter</strong> (Aid Planner) goes to the matter and its Extension check.</li>
            <li>Reminders clear themselves when the facts change: a new status or listing, an extension letter. <strong>An approved extension covers the rest of its stage.</strong></li>
          </ul>
        </Section>
      </div>

      <Section title="Keep in mind">
        <ul className="grid gap-2 text-xs text-slate-700 sm:grid-cols-2 print:grid-cols-2">
          <li className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2"><strong>Reminders are not sent to anyone.</strong> They show when the diary or the Aid Planner is opened. Use Add to Outlook to be reminded elsewhere.</li>
          <li className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2"><strong>They are only as good as what is entered:</strong> the funding status and diary code on each diary row; in the Aid Planner, the letters uploaded, the claims recorded and the next listing date and type.</li>
        </ul>
      </Section>

      <footer className="border-t border-slate-200 pt-2 text-[11px] text-slate-500">Complete Law Support portal · Not sure whether an extension is needed? Open the matter's Extension check in the Aid Planner.</footer>
    </div>
  );
}
